/**
 * Specialization utilities - helper functions for custom serialization registry.
 */
import { getClassDefaultObject, registerCdoSpecialization } from './cdo.js';
import { getClassForDeserialization, GlobalObjectRegistry, registerClassAlias } from './clsAlias.js';
import {property} from './decorator.js';
import { ISerializableObjectTag } from './serializable.js';

import type { PropertyMetadata } from './decorator.js';
import type { IDumper, ILoader } from './factories.js';
import type { ISerializableObject } from './serializable.js';
import type { Constructor } from '../../systems/ClassRegistry.js';

interface SerializationEntry<T extends object> {
  serializeFn?: (obj: T, dumper: IDumper) => any;
  deserializeFn?: (obj: T, loader: ILoader) => void;  // load data into existing instance
  staticDeserializeFn?: (data: any, loader: ILoader) => any;  // load data and return new instance
}
const serializationRegistry = new Map<Function, SerializationEntry<any>>();
const disallowPopulateClasses = new Set<Constructor>();

export function getSerializeFn<T extends object>(obj: T): ((obj: T | undefined, dumper: IDumper) => any) | undefined {
  if (!obj) return undefined;

  const entry = serializationRegistry.get(obj.constructor);
  return entry?.serializeFn;
}

export function getDeserializeFn<T extends object>(obj: T): ((obj: T | undefined, loader: ILoader) => void) | undefined {
  if (!obj) return undefined;

  const entry = serializationRegistry.get(obj.constructor);
  return entry?.deserializeFn;
}

export function getStaticDeserializeFn(cls: string | undefined): ((data: any, loader: ILoader) => any) | undefined {
  const constructor = cls ? getClassForDeserialization(cls) : undefined;
  if (!constructor) return undefined;

  const entry = serializationRegistry.get(constructor);
  return entry?.staticDeserializeFn;
}

export function allowPopulateExistingInstance(cls: Constructor): boolean {
  if (disallowPopulateClasses.has(cls)) return false;

  // if any of the parent classes disallow populate, also disallow
  let currentProto = Object.getPrototypeOf(cls.prototype);
  while (currentProto && currentProto !== Object.prototype) {
    const currentConstructor = currentProto.constructor;
    if (disallowPopulateClasses.has(currentConstructor)) {
      return false;
    }
    currentProto = Object.getPrototypeOf(currentProto);
  }

  return true;
}

interface SpecializationOptions<T> {
  cls: Constructor<T>;
  serializeFn?: (obj: T, dumper: IDumper) => void;
  deserializeFn?: (obj: T, loader: ILoader) => void;
  staticDeserializeFn?: (data: any, loader: ILoader) => T;
  clsAlias?: string;
  cdo?: T;  // class default object to use if this class can't be default constructed due to it having constructor arguments.
  disallowPopulate?: boolean;  // if true, deserializer will not be called to populate existing instance, and create new instance always
}

export function registerSpecialization<T>(
  options: SpecializationOptions<T>
): void {
  const { cls, serializeFn, deserializeFn, staticDeserializeFn, clsAlias, cdo, disallowPopulate } = options;
  serializationRegistry.set(cls, { serializeFn, deserializeFn, staticDeserializeFn });

  if (clsAlias) {
    registerClassAlias(cls, clsAlias);
  }

  if (disallowPopulate) {
    disallowPopulateClasses.add(cls);
  }

  if (cdo) {
    registerCdoSpecialization(cls, cdo);
  }
}

export const enum RegistrationFlags {
  None = 0,
  NoConstructor = 1 << 0,
  NoDeserializer = 1 << 1,
}

interface RegisterSimpleTypeOptions<T> {
  clsAlias?: string;
  flags?: RegistrationFlags;
}

type SimpleTypeConstructorParameters<C extends Constructor> = [...ConstructorParameters<C>];

export function registerSimpleType<C extends Constructor>(
  cls: C,
  propertiesGetter: (o: InstanceType<C>) => SimpleTypeConstructorParameters<C>,
  options?: RegisterSimpleTypeOptions<InstanceType<C>>
) {
  const { clsAlias, flags } = options ?? {};
  // this is just a cooler way to get property paths and set values
  // asking user to pass in `o.x, o.y, o.z` instead of `['x', 'y', 'z']`, or `o.params.foo` instead of `['params.foo']` to avoid typos
  const makePropertiesWriter = (obj: any) => {
    const paths: (string | number | Symbol)[][] = [];
    const createProxy = (target: any, currentPath: (string | number | Symbol)[] = []): any => {
      return new Proxy(target, {
        get(t, prop) {
          const nextPath = [...currentPath, prop];
          if (nextPath.length === 1) {
            paths.push(nextPath);
          }
          const val = t[prop];
          if (val !== null && typeof val === 'object') {
            return createProxy(val, nextPath);
          } else {
            return val;
          }
        }
      });
    };

    propertiesGetter(createProxy(obj) as InstanceType<C>);

    return (newValues: any[]) => {
      paths.forEach((p, i) => {
        let target = obj;
        for (let j = 0; j < p.length - 1; j++) {
          target = target[p[j] as string | number];
        }
        target[p[p.length - 1] as string | number] = newValues[i];
      });
    };
  };

  const effectiveFlags = flags ?? RegistrationFlags.None;
  const defaultObject = getClassDefaultObject(cls);
  registerSpecialization(
    {
      cls,
      serializeFn: (obj: InstanceType<C>, dumper: IDumper) => {
        const dumpValues = propertiesGetter(obj);
        if (defaultObject) {
          const dumpDefaultValues = propertiesGetter(defaultObject);
          if (JSON.stringify(dumpValues) === JSON.stringify(dumpDefaultValues)) {
            // CAUTION: skip serializing default values by checking JSON.stringify equality
            // This could cause issues if object properties are shared references
            return;
          }
        }
        dumper.dumpValue('_', dumpValues);
      },
      deserializeFn: (effectiveFlags & RegistrationFlags.NoDeserializer) ? undefined :
        (obj: InstanceType<C>, loader: ILoader) => {
          const values = loader.loadValue('_');
          if (defaultObject && values === undefined) {
            makePropertiesWriter(obj)(propertiesGetter(defaultObject));
          } else {
            makePropertiesWriter(obj)(values);
          }
        },
      staticDeserializeFn: (effectiveFlags & RegistrationFlags.NoConstructor) ? undefined :
        (data: any, loader: ILoader) => {
          if (data._ === undefined) {
            // suppose it can be constructed with no arguments
            return new cls();
          }
          const args = loader.loadValue('_');
          return new cls(...args);
        },
      clsAlias
    }
  );
}


export function registerPropertyMetadata<T extends object>(cls: Constructor<T>, metadata: Partial<Record<keyof T, PropertyMetadata>>): void {
  Object.entries(metadata).forEach(
    ([key, meta]) => property(meta as PropertyMetadata)(cls.prototype, key)
  );
}


registerSpecialization({
  cls: GlobalObjectRegistry,
  staticDeserializeFn: (data: any, loader: ILoader) => {
    const name = data['name'];
    return GlobalObjectRegistry.getObjectByName(name);
  },
  clsAlias: GlobalObjectRegistry.NAME,
});


export function registerAsGlobalObject<T extends object>(name: string, factory: () => T): void {
  GlobalObjectRegistry.registerFactory(name, () => {
    const obj = factory();
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      // mark it as serializable
      (obj as any)[ISerializableObjectTag] = true;
      (obj as ISerializableObject).serialize = (dumper: IDumper) => {
        dumper.dumpValue('name', name);
      };
    }
    return obj;
  });
}
